import { cn } from "@/utils";
import { ChevronRight, type LucideIcon } from "lucide-react";

/**
 * Always-visible disclosure chevron for expandable rail rows. Points right
 * when closed, rotates 90° when open. Rendered alongside the row's resource
 * icon — never in place of it.
 *
 * Rotation source: pass `open` when the caller owns the state. Omit it for
 * rows whose open state lives on an ancestor's `data-state` (e.g. the Radix
 * accordion trigger in `RailGroup`) — the ancestor must then carry the
 * `group/row` class.
 */
export function RailChevron({
  open,
  size = "size-3.5",
  className,
}: {
  open?: boolean;
  size?: string;
  className?: string;
}) {
  return (
    <ChevronRight
      aria-hidden
      className={cn(
        "shrink-0 text-n-40 transition-transform duration-150 ease-out",
        size,
        open === undefined
          ? "group-data-[state=open]/row:rotate-90"
          : open && "rotate-90",
        className,
      )}
    />
  );
}

/** Inert chevron-width spacer so leaf rows align with expandable siblings. */
export function RailChevronSpacer({ size = "size-3.5" }: { size?: string }) {
  return <span aria-hidden className={cn("shrink-0", size)} />;
}

/**
 * Leading cluster of an expandable row: `[chevron] [resource icon]`, gapped
 * tighter than the row's icon→label gap so the pair reads as one affordance.
 */
export function RailDisclosureIcon({
  open,
  icon: Icon,
  iconSize = "size-4",
  chevronSize,
}: {
  open?: boolean;
  icon: LucideIcon;
  iconSize?: string;
  chevronSize?: string;
}) {
  return (
    <span className="flex shrink-0 items-center gap-1.5">
      <RailChevron open={open} size={chevronSize} />
      <Icon className={cn("shrink-0 text-n-50", iconSize)} />
    </span>
  );
}
